import { useEffect, useState } from "react";
import axios from "axios";
import { getAuthHeader } from "../../utils/authHeader";

export const ADD_POST_FLAG_KEY = "add_post.new_page_enabled";

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;

async function fetchFlag(): Promise<boolean> {
    try {
        const headers = await getAuthHeader();
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/app-config/public`, { headers });
        const value = res.data?.[ADD_POST_FLAG_KEY];
        return value === false ? false : true; // fail open: only an explicit false turns the page off
    } catch {
        return true; // a 403 for a paused/inactive account, a 404, a network error: all leave the page on
    }
}

export function __resetAddPostFlagForTests(): void {
    cached = null;
    inflight = null;
}

export interface AddPostPageSwitch {
    /** True until the API explicitly says false (fail open). */
    enabled: boolean;
    /** The answer is in (or the request failed): `enabled` is the switch itself, not the fail-open default. */
    resolved: boolean;
}

/** One request per page load. Routing reads `enabled` (fail open); the sidebar waits for `resolved`. */
export function useAddPostPageSwitch(): AddPostPageSwitch {
    const [state, setState] = useState<AddPostPageSwitch>(() => ({ enabled: cached ?? true, resolved: cached !== null }));
    useEffect(() => {
        const settle = (v: boolean) => setState((s) => (s.enabled === v && s.resolved ? s : { enabled: v, resolved: true }));
        if (cached !== null) {
            settle(cached);
            return;
        }
        inflight =
            inflight ??
            fetchFlag().then((v) => {
                cached = v;
                return v;
            });
        let alive = true;
        void inflight.then((v) => {
            if (alive) settle(v);
        });
        return () => {
            alive = false;
        };
    }, []);
    return state;
}

/** One request per page load; true until the API explicitly says false. */
export function useAddPostPageEnabled(): boolean {
    return useAddPostPageSwitch().enabled;
}
